import React, { useState, useCallback } from 'react';
import styles from './styles.module.scss';
import { SlideInput, CapFeedback } from '../../types';
import { isValidPhone, isValidEmail, isValidZipCode, hasAllSameDigits } from '../../utils/helpers';
import type { OptionalConsentAnswers } from '../../../../shared/consent/OptionalConsents';

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';

interface FormInputProps {
  inputs: SlideInput[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onSubmit: () => void;
  submitLabel?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  backLabel?: string;
  isPhoneSlide?: boolean;
  isLocationSlide?: boolean;
  capFeedback?: CapFeedback | null;
  isCapValid?: boolean | null;
  optionalConsents?: OptionalConsentAnswers;
  onOptionalConsentsChange?: (answers: OptionalConsentAnswers) => void;
  onPhoneContinue?: (answers: OptionalConsentAnswers) => void;
}

interface PhoneConsentsProps {
  answers: OptionalConsentAnswers;
  phoneValid: boolean;
  onChange: (answers: OptionalConsentAnswers) => void;
  onContinue: (answers: OptionalConsentAnswers) => void;
}

const PhoneConsents: React.FC<PhoneConsentsProps> = ({
  answers,
  phoneValid,
  onChange,
  onContinue,
}) => {
  const partnerChecked = answers.thirdPartyMarketing === true;

  const togglePartner = () => {
    onChange({ ...answers, thirdPartyMarketing: !partnerChecked });
  };

  const handleContinue = () => {
    onContinue(answers);
  };

  return (
    <div className={styles.phoneConsents}>
      <p className={styles.consentHint}>Facoltativi, revocabili in ogni momento</p>

      <label className={styles.partnerCard}>
        <span className={styles.partnerCardTop}>
          <span className={styles.partnerTitle}>Comunicazione dei dati ai partner</span>
          <input
            type="checkbox"
            className={`${styles.consentCheckbox} ${
              phoneValid && !partnerChecked ? styles.consentCheckboxReady : ''
            }`}
            checked={partnerChecked}
            onChange={togglePartner}
          />
        </span>
        <span className={styles.partnerDescription}>
          Acconsento a comunicare i miei dati ai partner in{' '}
          <a
            className={styles.privacyLink}
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            elenco
          </a>
          , per ricevere le loro offerte. Agiscono come titolari autonomi.
        </span>
      </label>

      <button
        type="button"
        className={`${styles.discoverCta} ${phoneValid ? styles.discoverCtaReady : ''}`}
        onClick={handleContinue}
      >
        Scopri il tuo risparmio
      </button>

      <p className={styles.callNote}>
        Ti chiameremo per il preventivo; la chiamata può essere registrata.{' '}
        <a
          className={styles.privacyLink}
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Informativa privacy
        </a>
      </p>
    </div>
  );
};

/**
 * Form con validazione; slide CAP con feedback bonus.
 * Slide telefono: consenso ai partner e CTA che si attiva solo se il numero è valido e la casella è spuntata.
 */
const FormInput: React.FC<FormInputProps> = ({
  inputs,
  values,
  onChange,
  onSubmit,
  submitLabel = 'Avanti',
  showBackButton = true,
  onBack,
  backLabel = 'Indietro',
  isPhoneSlide = false,
  isLocationSlide = false,
  capFeedback,
  isCapValid,
  optionalConsents,
  onOptionalConsentsChange,
  onPhoneContinue,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateInput = useCallback((input: SlideInput, value: string): string => {
    if (input.required && !value.trim()) {
      return 'Campo obbligatorio';
    }

    switch (input.name) {
      case 'phone':
        if (value && !isValidPhone(value)) {
          if (hasAllSameDigits(value)) {
            return 'Il numero non può avere tutte le cifre uguali.';
          }
          return 'Numero di telefono non valido';
        }
        break;
      case 'email':
        if (value && !isValidEmail(value)) {
          return 'Indirizzo email non valido';
        }
        break;
      case 'zip':
        if (value && !isValidZipCode(value)) {
          return 'CAP non valido (5 cifre)';
        }
        break;
    }

    if (input.pattern && value) {
      const regex = new RegExp(input.pattern);
      if (!regex.test(value)) {
        return 'Formato non valido';
      }
    }

    return '';
  }, []);

  const handleChange = (input: SlideInput, value: string) => {
    onChange(input.name, value);

    if (touched[input.name]) {
      const error = validateInput(input, value);
      setErrors((prev) => ({ ...prev, [input.name]: error }));
    }
  };

  const handleBlur = (input: SlideInput) => {
    setTouched((prev) => ({ ...prev, [input.name]: true }));
    const error = validateInput(input, values[input.name] || '');
    setErrors((prev) => ({ ...prev, [input.name]: error }));
  };

  const isFormValid = useCallback(() => {
    const inputsValid = inputs.every((input) => {
      const value = values[input.name] || '';
      const error = validateInput(input, value);
      return !error;
    });

    if (isLocationSlide) {
      return inputsValid && isCapValid === true;
    }

    return inputsValid;
  }, [inputs, values, validateInput, isLocationSlide, isCapValid]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    setErrors(newErrors);
    setTouched(
      inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {})
    );

    if (isPhoneSlide && optionalConsents && onPhoneContinue) {
      submitPhone(optionalConsents);
      return;
    }

    if (!hasErrors && isFormValid()) {
      onSubmit();
    }
  };

  const submitPhone = (answers: OptionalConsentAnswers) => {
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    setErrors(newErrors);
    setTouched(inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {}));

    if (hasErrors || !isFormValid() || !onPhoneContinue) {
      return;
    }

    const submitted: OptionalConsentAnswers = {
      enpalMarketingConsent: false,
      thirdPartyMarketing: answers.thirdPartyMarketing === true,
      thirdPartyProfiling: false,
    };
    onOptionalConsentsChange?.(submitted);
    onPhoneContinue(submitted);
  };

  const renderCapFeedback = () => {
    if (!isLocationSlide) return null;

    const zipValue = values['zip'] || '';

    if (zipValue.length < 5) {
      return null;
    }

    if (isCapValid === false) {
      return (
        <div className={`${styles.capFeedbackMessage} ${styles.invalid}`}>
          ❌ CAP non valido
        </div>
      );
    }

    if (isCapValid === true && capFeedback) {
      return (
        <div className={`${styles.capFeedbackMessage} ${styles.valid}`}>
          <span className={styles.bonusCount}>{capFeedback.bonusCount} bonus</span> disponibili per il CAP{' '}
          <strong>{zipValue}</strong>
        </div>
      );
    }

    return null;
  };

  const getAutocomplete = (name: string): string => {
    switch (name) {
      case 'name':
        return 'name';
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      case 'zip':
        return 'postal-code';
      default:
        return 'off';
    }
  };

  const getInputLabel = (input: SlideInput): string | null => {
    if (isPhoneSlide && input.name === 'phone') {
      return 'Numero di telefono *';
    }
    return null;
  };

  return (
    <form className={styles.formGroup} onSubmit={handleSubmit}>
      {isLocationSlide && (
        <div className={styles.capFeedbackContainer}>
          {renderCapFeedback()}
        </div>
      )}

      {inputs.map((input) => {
        const label = getInputLabel(input);

        return (
          <div key={input.name} className={styles.inputWrapper}>
            {label && <label className={styles.inputLabel}>{label}</label>}
            <div className={isLocationSlide && input.name === 'zip' ? styles.inputWithCheck : ''}>
              <input
                className={`${styles.input} ${
                  touched[input.name] && errors[input.name] ? styles.inputError : ''
                } ${isPhoneSlide && input.name === 'phone' ? styles.phoneInput : ''}`}
                type={input.type}
                name={input.name}
                placeholder={input.placeholder}
                value={values[input.name] || ''}
                onChange={(e) => handleChange(input, e.target.value)}
                onBlur={() => handleBlur(input)}
                required={input.required}
                maxLength={input.maxLength}
                autoComplete={getAutocomplete(input.name)}
              />
              {isLocationSlide && input.name === 'zip' && values['zip']?.length === 5 && (
                <span
                  className={`${styles.checkIcon} ${
                    isCapValid === true ? styles.valid : isCapValid === false ? styles.invalid : ''
                  }`}
                >
                  {isCapValid === true ? '✓' : isCapValid === false ? '✗' : ''}
                </span>
              )}
            </div>
            {touched[input.name] && errors[input.name] && (
              <p className={styles.errorMessage}>{errors[input.name]}</p>
            )}
          </div>
        );
      })}

      {!isPhoneSlide && (
        <div className={styles.buttonGroup}>
          {showBackButton && onBack && (
            <button type="button" className={styles.btnPrev} onClick={onBack}>
              {backLabel}
            </button>
          )}
          <button type="submit" className={styles.btnNext} disabled={!isFormValid()}>
            {submitLabel}
          </button>
        </div>
      )}

      {isPhoneSlide && optionalConsents && onOptionalConsentsChange && onPhoneContinue && (
        <PhoneConsents
          answers={optionalConsents}
          phoneValid={isFormValid()}
          onChange={onOptionalConsentsChange}
          onContinue={submitPhone}
        />
      )}
    </form>
  );
};

export default FormInput;
